<template>
  <div class="file-chip-list">

    <PictureCarousel
      v-if="displayFiles && displayedFiles.length"
      :file-ids="value"
      no-text
      @pictureClick="onChipClick(null, null, $event)"
    />

    <div
      v-if="uploadZone"
      class="file-chip-list__upload-zone h-v-centerer generic-border--clickable"
      :class="{
        'file-chip-list__upload-zone--dragging-over': draggingFileOver,
        'file-chip-list__upload-zone--minimal': displayedFiles.length > 0,
        'file-chip-list__upload-zone--disabled': !editable,
      }"
      @drop="onFileDrop"
      @click="onAddFile"
      @dragover.prevent="draggingFileOver = true"
      @dragleave.prevent="draggingFileOver = false"
    >
      <Icon>{{ upload }}</Icon>
      <div v-if="uploadZone" class="file-chip-list__upload-zone__text">
        {{ $t('message.ADD_FILES') }}
      </div>
    </div>

    <div v-if="!displayFiles" class="file-chip-list__chips" :class="classes">
      <Chip
        v-for="(file, idx) in displayedFiles"
        v-show="file.status === 'success'"
        :key="file.id"
        v-quick-title="file.name"
        class="file-chip-list__chips__chip"
        :closable="editable"
        small
        @click="onChipClick($event, file, idx)"
        @input="confirmRemoveFile(file)"
      >
        <Icon
          class="file-chip-list__chips__icon"
          :color="getFileIconInfo(file.type).color"
          left
          :size="20"
        >
          {{ getFileIconInfo(file.type).icon }}
        </Icon>
        <span class="file-chip-list__chips__text">{{ file.name }}</span>
      </Chip>
      <!-- Remainder chip -->
      <Chip
        v-if="remainderFiles.length > 0"
        v-quick-title="remainderText"
        small
        @click="onChipClick($event)"
      >
        {{ `+ ${remainderFiles.length}` }}
      </Chip>
      <!-- Don't show the add button if there's an upload zone -->
      <button
        v-if="editable && !(fileDetails.length === 0 && uploadZone)"
        type="button"
        class="file-chip-list__chips__add-btn"
        @click="onAddFile"
      >
        <Icon image :left="addButtonHasText" :size="20">{{ '/images/add.png' }}</Icon>
        <span v-if="addButtonHasText">{{ $t('message.ADD_FILES') }}</span>
      </button>
    </div>

    <!-- Files edition -->
    <transition name="fade">
      <FileViewer
        v-if="fileViewerVisible"
        ref="fileViewer"
        :value="value"
        :editable="editable"
        :index="fileViewerIndex"
        :pathname="pathname"
        :init-with-additional-files="filesDraggedOver"
        @input="$emit('input', $event)"
        @close="fileViewerVisible = false"
      />
    </transition>
  </div>
</template>

<script>
/**
 * List of chip, representing files in the system
 *
 * @vue-prop {Boolean} [column] - If chip overflow, will display chips on the next line
 * @vue-prop {Boolean} [editable] - Display edition capability (close button on chips, add file btn)
 * @vue-prop {Boolean} [forceButtonText] - If true, add 'Add files' text to the add button
 *                                         Else, text only present if there's no files in the list
 * @vue-prop {Boolean} [displayFiles] - Show a thumbnail of files
 * @vue-prop {Boolean} [uploadZone] - Add a zone where the user cand rop files
 * @vue-prop {Number} [limit=Infinity] - Maximum of chips to display.
 *                                       Remaining files will be display as a single chip.
 * @vue-prop {Number[]} values - List of file ids
 *
 * @vue-event {Object} chipClick - Triggered when file chip is clicked
 * @vue-event {Number} removeFile - Request file remove ; Emit file ID
 */
import { createNamespacedHelpers } from 'vuex';
// Import components
import Chip from '@/components/chip.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import Icon from '@/components/icon.vue';
import PictureCarousel from '@/components/picture-carousel.vue';
// Import Vuex utils
import { GET_FILE_DETAILS } from '@/store/modules/file/action-types';
// Import utils
import { calert } from '@/utils/main';
import { getFileIconInfo } from '@/utils/file';
import { upload } from '@/assets/icons';


const { mapActions, mapState } = createNamespacedHelpers('file');

export default {

  name: 'FileChipList',

  components: {
    Chip,
    FileViewer,
    Icon,
    PictureCarousel,
  },

  props: {
    column: Boolean,
    editable: Boolean,
    forceButtonText: Boolean,
    ownFileViewer: Boolean,
    displayFiles: Boolean,
    uploadZone: Boolean,
    limit: {
      type: Number,
      default: Infinity,
      validator: v => v >= 0,
    },
    value: {
      type: Array,
      default: () => [],
    },
    pathname: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      upload,
      draggingFileOver: false,
      filesDraggedOver: null,
      fileViewerVisible: false,
      fileViewerIndex: 0,
    };
  },

  computed: {
    classes() {
      return {
        'file-chip-list__chips--column': this.column,
      };
    },
    fileDetails() {
      return this.value.map(fid => this.fileDetailsData[fid] || {
        id: fid,
        status: 'pending',
      });
    },
    displayedFiles() {
      return this.fileDetails.slice(0, Math.max(0, Math.min(this.fileDetails.length, this.limit)));
    },
    remainderFiles() {
      return this.fileDetails.slice(this.displayedFiles.length);
    },
    remainderText() {
      // List of files displayed as a tooltip on the remainder chip
      return this.remainderFiles
        .map(f => `• ${f.name}`)
        .join('\n');
    },
    /** Boolean: Should the add button display 'add files' string */
    addButtonHasText() {
      return this.forceButtonText || this.fileDetails.length === 0;
    },
    ...mapState({
      fileDetailsData: state => state.files,
    }),
  },

  watch: {
    value() {
      this.fetchFileDetails();
    },
    fileViewerVisible() {
      this.$emit('openFileViewer', this.fileViewerVisible);
    },
  },

  created() {
    this.fetchFileDetails();
  },

  methods: {
    getFileIconInfo,
    fetchFileDetails() {
      this.GET_FILE_DETAILS({ ids: this.value });
    },
    /** Prompt the user to confirm the file deletion */
    confirmRemoveFile(file) {
      calert(this.$t('message.CONFIRM_DELETE_FILE'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.removeFile(file);
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Remove the file and emit the new value */
    removeFile(file) {
      this.$emit('removeFile', file.id);
      const idx = this.value.indexOf(file.id);
      if (idx !== -1) {
        const output = this.value.slice();
        output.splice(idx, 1);
        this.$emit('input', output);
      }
    },
    /** Handle clicking on the 'add' button  */
    onAddFile() {
      if (!this.editable) {
        return;
      }
      if (this.ownFileViewer && this.pathname !== null) {
        this.fileViewerIndex = 0;
        this.fileViewerVisible = true;
        this.$nextTick(() => {
          this.$refs.fileViewer.addFile();
        })
      } else {
        this.$emit('addFile')
      }
    },
    /** Handle clicking on one of the chips  */
    onChipClick(event, file, idx) {
      if (this.ownFileViewer && this.pathname !== null) {
        this.fileViewerIndex = idx || 0;
        this.fileViewerVisible = true;
      } else {
        this.$emit('chipClick', { event, file })
      }
    },
    /** Handle dropping a file on the upload-zone */
    onFileDrop(evt) {
      if (!this.editable) {
        return;
      }
      this.draggingFileOver = false;
      if (evt.dataTransfer.items) {
        const items = Array.from(evt.dataTransfer.items);
        this.filesDraggedOver = items
          .filter(item => item.kind === 'file')
          .map(item => item.getAsFile());
        if (this.filesDraggedOver && this.filesDraggedOver.length) {
          this.fileViewerVisible = true;
        }
      }
      evt.preventDefault();
    },
    ...mapActions({
      GET_FILE_DETAILS,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.file-chip-list {

  .picture-carousel {
    margin-bottom: 0.5rem;

    &__current-picture img {
      max-height: 7rem;
    }
  }

  &__upload-zone {

    &.generic-border--clickable {
      border-radius: $main-border-radius;
      border: dashed 1px $color-border-grey-dark;
      box-sizing: border-box;
      flex-flow: column;
      padding: 1rem;
      background-color: transparent;
      // Keep the transition of generic border
      transition: background-color 0.2s, box-shadow 0.2s;
    }

    .icon {
      background: $color-shizen-light-cyan;
      border-radius: 2rem;
      padding: .65rem;
      width: 1.2rem;
      height: 1.2rem;
      overflow: visible;
    }

    &__text {
      font-weight: 600;
      margin-top: .8rem;
      font-size: 0.9rem;
    }

    &--dragging-over.generic-border--clickable {
      background-color: $color-shizen-light-cyan;
    }

    &--minimal.generic-border--clickable {
      flex-flow: row;
      padding: .4rem;

      .icon {
        margin-right: 1rem;
        padding: .5rem;
        width: .8rem;
        height: .8rem;
      }

      .file-chip-list__upload-zone__text {
        margin-top: 0;
      }
    }

    &--disabled {
      &.generic-border--clickable {
        cursor: not-allowed;
      }

      .icon, .file-chip-list__upload-zone__text {
        opacity: 0.55;
      }

    }
  }

  &__chips {
    align-content: flex-start;
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    overflow: auto;

    &--column {
      flex-wrap: wrap;
    }
    &__icon {
      flex: 0 0 auto;
    }
    &__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1 1 auto;
    }
    &__chip {
      box-sizing: border-box;
      min-width: 70px;
      max-width: 220px;

      &:first-child {
        margin-left: 0;
      }
      &:last-child  {
        margin-right: 0;
      }
    }
    &__add-btn {
      // @extend .add-button
      align-items: center;
      background: none;
      border: none;
      color: #58595B;
      display: flex;
      justify-content: center;
      padding: 1px 3px;
      vertical-align: top;

      &:hover {
        background: $color-hoverbtn-light-blue;
        cursor: pointer;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .file-chip-list__chips__chip {
    max-width: calc(25% - #{$chip-margin * 2});
  }
}

@media (max-width: $smartphone-portrait) {
  .file-chip-list__chips__chip {
    max-width: calc(50% - #{$chip-margin * 2});
  }
}
</style>
